import React, { forwardRef, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { ITEM_TYPE_KEY_TO_ROUTES } from '@atlassian/jira-business-navigation-common/src/index.tsx';
import { getRouteForViewType } from '@atlassian/jira-business-navigation-common/src/utils.tsx';
import type { HideNavigationItemArgs } from '@atlassian/jira-navigation-kit-hide-item-service-old/src/services/index.tsx';
import Tab from '@atlassian/jira-navigation-kit-tab-old/src/ui/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { JiraNavigationItemTypeKey } from '@atlassian/jira-relay/src/__generated__/ui_navigationKitTabListOld_TabList.graphql';
// eslint-disable-next-line camelcase
import { DEPRECATED_DO_NOT_USE_projectDetailsResource } from '@atlassian/jira-router-resources-business-project-details/src/index.tsx';
import { useResource, useRouter, useRouterActions } from '@atlassian/react-resource-router';
import type { simpleNavigationTab_businessTabs$key } from '@atlassian/jira-relay/src/__generated__/simpleNavigationTab_businessTabs.graphql';
import { TabIcon } from '../../common/ui/tab-icon/index.tsx';
import { useUpdateNavigationDefaultView } from '../../controllers/update-navigation-default-view/index.tsx';
import { AppNavigationTab } from './app-navigation-tab/index.tsx';
import { ShortcutNavigationTab } from './shortcut-navigation-tab/index.tsx';

type Props = {
	tabFragment: simpleNavigationTab_businessTabs$key;
	onHideTab: (args: HideNavigationItemArgs) => void;
	position: number;
	id: string;
	beforeItemId: string | null;
	afterItemId: string | null;
	defaultViewTypeKey?: JiraNavigationItemTypeKey;
};

/**
 * @deprecated remove when cleaning up getWillShowNav4
 */
export const SimpleNavigationTab = forwardRef(
	(
		{ tabFragment, position, id, onHideTab, beforeItemId, afterItemId, defaultViewTypeKey }: Props,
		ref: React.Ref<HTMLElement>,
	) => {
		const { data: projectDetails } = useResource(DEPRECATED_DO_NOT_USE_projectDetailsResource);
		const projectKey = projectDetails?.currentProject?.key || '';
		const route = useCurrentRoute();
		const { push, replace } = useRouterActions();
		const [routerState] = useRouter();
		const { name: routeName } = routerState.route;
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const updateNavigationDefaultView = useUpdateNavigationDefaultView(projectKey);

		const tabData = useFragment(
			graphql`
				fragment simpleNavigationTab_businessTabs on JiraNavigationItem {
					typeKey @required(action: THROW)
					...ui_navigationKitTabOld_Tab
					...appNavigationTab_businessTabs
					...shortcutNavigationTab_businessTabs_OldShortcutNavigationTab
				}
			`,
			tabFragment,
		);

		const canEdit = projectDetails?.currentProject?.permissions.administerProject;

		const handleTabClick = useCallback(
			(itemTypeKey: JiraNavigationItemTypeKey, extra?: Object) => {
				fireUIAnalytics(createAnalyticsEvent({}), 'tab clicked', 'navigationItem', {
					viewType: itemTypeKey.toLowerCase(),
					...extra,
				});
			},
			[createAnalyticsEvent],
		);

		const handleDrag = useCallback(
			(itemTypeKey: JiraNavigationItemTypeKey) => {
				fireUIAnalytics(createAnalyticsEvent({}), 'tab dragged', 'navigationItem', {
					viewType: itemTypeKey.toLowerCase(),
				});
			},
			[createAnalyticsEvent],
		);

		const onHideTabCompleted = useCallback(
			(isCurrentRoute: boolean) => () => {
				// Do not redirect to default view if the current route is not the hidden view
				if (!isCurrentRoute) {
					return;
				}
				if (projectDetails?.currentProject) {
					const redirectPath =
						(defaultViewTypeKey && getRouteForViewType(projectKey, defaultViewTypeKey)) ??
						`/projects/${projectKey}`;
					if (routeName.includes('legacy')) {
						replace(redirectPath);
						push(redirectPath);
					} else {
						replace(redirectPath);
					}
				}
			},
			[defaultViewTypeKey, projectDetails, projectKey, push, replace, routeName],
		);

		const onSetAsDefaultCompleted = useCallback(
			() => updateNavigationDefaultView(tabData.typeKey),
			[tabData.typeKey, updateNavigationDefaultView],
		);

		switch (tabData.typeKey) {
			case 'APPS':
				return (
					<AppNavigationTab
						tabFragment={tabData}
						position={position}
						id={id}
						onTabClick={handleTabClick}
						canEdit={canEdit}
						handleDrag={() => handleDrag(tabData.typeKey)}
						afterItemId={afterItemId}
						beforeItemId={beforeItemId}
					/>
				);
			case 'SHORTCUTS': {
				const isSelected = route.name === 'classic-business-shortcut';
				return (
					<ShortcutNavigationTab
						isSelected={isSelected}
						tabFragment={tabData}
						position={position}
						id={id}
						onHideTab={(args) => {
							fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'hideViewOption', {
								viewType: tabData.typeKey.toLowerCase(),
							});
							onHideTab({ ...args, onCompleted: onHideTabCompleted(isSelected) });
						}}
						onTabClick={handleTabClick}
						canEdit={canEdit}
						handleDrag={() => handleDrag(tabData.typeKey)}
						afterItemId={afterItemId}
						beforeItemId={beforeItemId}
					/>
				);
			}
			default: {
				if (ITEM_TYPE_KEY_TO_ROUTES.has(tabData.typeKey)) {
					const routes = ITEM_TYPE_KEY_TO_ROUTES.get(tabData.typeKey);
					const isSelected = routes?.has(route.name) || routes?.has(route.group ?? '') || false;
					return (
						<Tab
							isSelected={isSelected}
							onHideTab={(args) => {
								fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'hideViewOption', {
									viewType: tabData.typeKey.toLowerCase(),
								});
								onHideTab({ ...args, onCompleted: onHideTabCompleted(isSelected) });
							}}
							ref={ref}
							tabFragment={tabData}
							elemBefore={<TabIcon typeKey={tabData.typeKey} label="" size="small" />}
							href={getRouteForViewType(projectKey, tabData.typeKey)}
							position={position}
							id={id}
							onSetAsDefaultCompleted={onSetAsDefaultCompleted}
							canEdit={canEdit}
							handleDrag={() => handleDrag(tabData.typeKey)}
							onClick={() => handleTabClick(tabData.typeKey)}
							beforeItemId={beforeItemId}
							afterItemId={afterItemId}
						/>
					);
				}
				return null;
			}
		}
	},
);
